import { useState, useEffect } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  CheckCircle, 
  Building2, 
  Home, 
  Sparkles, 
  Trees, 
  Wrench,
  Users,
  Clock,
  Shield,
  Award,
  Send,
  Briefcase,
  ChevronRight,
  ArrowLeft,
  Star,
  Quote,
  ArrowRight,
  Paintbrush
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import AngebotPage from "./AngebotPage";

// SEO Data
const SEO_DATA = {
  services: [
    { 
      slug: "gebaeudereinigung", 
      name: "Gebäudereinigung", 
      title: "Gebäudereinigung Frankfurt | Professionelle Reinigung",
      description: "Professionelle Gebäudereinigung in Frankfurt am Main. Wir reinigen Bürogebäude, Wohnanlagen und Gewerbeimmobilien. ✓ Seit 2002 ✓ Zuverlässig ✓ Fair",
      icon: Building2,
      heroText: "Professionelle Gebäudereinigung für Ihre Immobilie",
      content: "Unsere erfahrenen Teams sorgen für makellose Sauberkeit in Ihrem Gebäude. Von der regelmäßigen Unterhaltsreinigung bis zur Grundreinigung - wir bieten maßgeschneiderte Lösungen für jede Anforderung.",
      benefits: ["Regelmäßige Unterhaltsreinigung", "Grundreinigung", "Fassadenreinigung", "Tiefgaragenreinigung", "Flexible Reinigungszeiten", "Eigenes Reinigungspersonal"]
    },
    { 
      slug: "treppenhausreinigung", 
      name: "Treppenhausreinigung", 
      title: "Treppenhausreinigung Frankfurt | Saubere Treppenhäuser",
      description: "Treppenhausreinigung in Frankfurt und Umgebung. Regelmäßige Reinigung für Mehrfamilienhäuser und Wohnanlagen. ✓ Zuverlässig ✓ Gründlich ✓ Fair",
      icon: Sparkles,
      heroText: "Saubere Treppenhäuser für zufriedene Bewohner",
      content: "Ein sauberes Treppenhaus ist die Visitenkarte Ihres Hauses. Wir übernehmen die regelmäßige Reinigung und sorgen für einen gepflegten Eindruck bei Bewohnern und Besuchern.",
      benefits: ["Wöchentliche Reinigung", "Bodenreinigung & Wischen", "Handlauf & Geländer", "Briefkastenanlage", "Eingangstüren", "Fenster im Treppenhaus"]
    },
    { 
      slug: "bueroreinigung", 
      name: "Büroreinigung", 
      title: "Büroreinigung Frankfurt | Saubere Arbeitsplätze",
      description: "Professionelle Büroreinigung in Frankfurt am Main. Saubere Büros für produktives Arbeiten. ✓ Flexible Zeiten ✓ Gründlich ✓ Zuverlässig",
      icon: Briefcase,
      heroText: "Saubere Büros für produktives Arbeiten",
      content: "Ein sauberes Büro steigert die Produktivität und das Wohlbefinden Ihrer Mitarbeiter. Wir reinigen nach Feierabend oder am Wochenende - ganz nach Ihren Wünschen.",
      benefits: ["Schreibtische & Arbeitsplätze", "Sanitäranlagen", "Küchen & Aufenthaltsräume", "Böden & Teppiche", "Papierkörbe & Mülltrennung", "Flexible Reinigungszeiten"]
    },
    { 
      slug: "fensterreinigung", 
      name: "Fensterreinigung", 
      title: "Fensterreinigung Frankfurt | Glasreinigung",
      description: "Professionelle Fensterreinigung und Glasreinigung in Frankfurt. Streifenfreie Sauberkeit für klare Sicht. ✓ Innen & Außen ✓ Alle Höhen",
      icon: Sparkles,
      heroText: "Streifenfreie Fenster für klare Sicht",
      content: "Saubere Fenster lassen mehr Licht herein und verbessern das Erscheinungsbild Ihrer Immobilie. Wir reinigen Fenster in allen Höhen - streifenfrei und gründlich.",
      benefits: ["Innen- & Außenreinigung", "Rahmen & Falze", "Glasfassaden", "Wintergärten", "Schaufenster", "Regelmäßige Intervalle"]
    },
    { 
      slug: "malerarbeiten", 
      name: "Malerarbeiten", 
      title: "Malerarbeiten Frankfurt | Innenanstriche für Wohnung & Büro",
      description: "Malerarbeiten in Frankfurt für Wohnungen, Büros, Praxen, Studios und Treppenhäuser. Innenanstriche, Abdecken und kleinere Vorarbeiten. Unverbindlich kalkulieren.",
      icon: Paintbrush,
      heroText: "Saubere Innenanstriche für Wohnungen, Büros und Gewerberäume",
      content: "Wir bieten Malerarbeiten im Innenbereich für Wohnungen, Häuser, Büros, Praxen und Studios an. Der Umfang wird anhand Ihrer Angaben, Fotos oder bei Bedarf bei einer Besichtigung abgestimmt.",
      benefits: ["Innenanstriche für Wände", "Wände und Decken", "Abdecken von Böden und Möbeln", "Kleinere übliche Vorarbeiten", "Weiße und farbige Anstriche", "Kalkulation per Foto oder Besichtigung"]
    },
    { 
      slug: "wohnungsreinigung", 
      name: "Wohnungsreinigung", 
      title: "Wohnungsreinigung Frankfurt | Endreinigung & Umzug",
      description: "Wohnungsreinigung in Frankfurt. Endreinigung bei Auszug, Grundreinigung, regelmäßige Reinigung. ✓ Professionell ✓ Gründlich ✓ Fair",
      icon: Home,
      heroText: "Professionelle Reinigung für Ihre Wohnung",
      content: "Ob Endreinigung bei Auszug, Grundreinigung oder regelmäßige Wohnungsreinigung - wir sorgen für Sauberkeit in Ihrem Zuhause oder Ihrer Mietwohnung.",
      benefits: ["Endreinigung bei Auszug", "Grundreinigung", "Regelmäßige Reinigung", "Küche & Bad intensiv", "Fenster inklusive", "Bezugsfertige Übergabe"]
    },
    { 
      slug: "hausmeisterservice", 
      name: "Hausmeisterservice", 
      title: "Hausmeisterservice Frankfurt | Facility Management",
      description: "Zuverlässiger Hausmeisterservice in Frankfurt. Betreuung von Wohnanlagen und Gewerbeimmobilien. ✓ Seit 2002 ✓ Alles aus einer Hand",
      icon: Wrench,
      heroText: "Kompletter Hausmeisterservice für Ihre Immobilie",
      content: "Unser Hausmeisterservice kümmert sich um alle Belange rund um Ihre Immobilie. Von Kleinreparaturen bis zur Kontrolle der Haustechnik - wir sind Ihr zuverlässiger Partner.",
      benefits: ["Kleinreparaturen", "Haustechnik-Kontrolle", "Müllbereitstellung", "Kontrollgänge", "Schlüsselverwaltung", "Notdienst-Erreichbarkeit"]
    },
    { 
      slug: "winterdienst", 
      name: "Winterdienst", 
      title: "Winterdienst Frankfurt | Schneeräumung & Streudienst",
      description: "Zuverlässiger Winterdienst in Frankfurt und Umgebung. Schneeräumung und Streudienst für sichere Wege. ✓ 24/7 Bereitschaft ✓ Schnell vor Ort",
      icon: Trees,
      heroText: "Sicherer Winterdienst für Ihre Immobilie",
      content: "Im Winter sorgen wir für sichere Gehwege und Zufahrten. Unser Winterdienst ist bei Schneefall schnell vor Ort und räumt zuverlässig.",
      benefits: ["Schneeräumung", "Streudienst", "Gehwege & Zufahrten", "24/7 Bereitschaft", "Dokumentation", "Haftungsübernahme"]
    },
    { 
      slug: "gartenpflege", 
      name: "Gartenpflege", 
      title: "Gartenpflege Frankfurt | Grünanlagenpflege",
      description: "Professionelle Gartenpflege und Grünanlagenpflege in Frankfurt. Gepflegte Außenanlagen für Ihre Immobilie. ✓ Regelmäßig ✓ Zuverlässig",
      icon: Trees,
      heroText: "Gepflegte Außenanlagen das ganze Jahr",
      content: "Schöne Außenanlagen steigern den Wert Ihrer Immobilie. Wir übernehmen die regelmäßige Pflege von Gärten, Grünflächen und Außenanlagen.",
      benefits: ["Rasenmähen", "Heckenschnitt", "Unkrautentfernung", "Laubbeseitigung", "Beetpflege", "Baum- & Strauchschnitt"]
    }
  ],
  locations: [
    { slug: "frankfurt", name: "Frankfurt am Main", region: "Zentrum" },
    { slug: "frankfurt-bockenheim", name: "Frankfurt Bockenheim", region: "Frankfurt" },
    { slug: "frankfurt-westend", name: "Frankfurt Westend", region: "Frankfurt" },
    { slug: "frankfurt-nordend", name: "Frankfurt Nordend", region: "Frankfurt" },
    { slug: "frankfurt-sachsenhausen", name: "Frankfurt Sachsenhausen", region: "Frankfurt" },
    { slug: "frankfurt-bornheim", name: "Frankfurt Bornheim", region: "Frankfurt" },
    { slug: "frankfurt-ostend", name: "Frankfurt Ostend", region: "Frankfurt" },
    { slug: "frankfurt-gallus", name: "Frankfurt Gallus", region: "Frankfurt" },
    { slug: "offenbach", name: "Offenbach am Main", region: "Umgebung" },
    { slug: "bad-homburg", name: "Bad Homburg", region: "Umgebung" },
    { slug: "eschborn", name: "Eschborn", region: "Umgebung" },
    { slug: "oberursel", name: "Oberursel", region: "Umgebung" },
    { slug: "kronberg", name: "Kronberg im Taunus", region: "Umgebung" },
    { slug: "neu-isenburg", name: "Neu-Isenburg", region: "Umgebung" },
    { slug: "dreieich", name: "Dreieich", region: "Umgebung" },
    { slug: "langen", name: "Langen", region: "Umgebung" },
    { slug: "maintal", name: "Maintal", region: "Umgebung" },
    { slug: "hanau", name: "Hanau", region: "Umgebung" }
  ]
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Navigation Component
const Navigation = ({ isSubpage = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = isSubpage ? [
    { href: "/", label: "Startseite" },
    { href: "/leistungen", label: "Leistungen" },
    { href: "/standorte", label: "Standorte" },
    { href: "/#kontakt", label: "Kontakt" },
  ] : [
    { href: "#ueber-uns", label: "Über Uns" },
    { href: "#leistungen", label: "Leistungen" },
    { href: "#warum-wir", label: "Warum Wir" },
    { href: "#jobs", label: "Jobs" },
    { href: "#faq", label: "FAQ" },
    { href: "#kontakt", label: "Kontakt" },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    if (href.startsWith("/#")) {
      navigate("/");
      setTimeout(() => {
        const element = document.querySelector(href.replace("/", ""));
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(href);
    }
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isSubpage ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            <img src="/logo.png" alt="Frankfurt Reinigung 24 Logo" className="h-12 w-auto" />
            <span className="font-bold text-lg text-[#0F172A] hidden sm:block">
              Frankfurt Reinigung 24
            </span>
          </Link>

          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="nav-link text-[#334155] hover:text-[#0066CC] font-medium"
              >
                {link.label}
              </a>
            ))}
            <Button
              onClick={() => navigate("/angebot")}
              className="bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full px-6"
              data-testid="nav-cta-button"
            >
              Angebot anfragen
            </Button>
          </div>

          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white rounded-2xl mb-4 p-4 shadow-lg">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="block py-3 px-4 text-[#334155] hover:text-[#0066CC] hover:bg-slate-50 rounded-lg font-medium"
              >
                {link.label}
              </a>
            ))}
            <Button
              onClick={() => { setIsMobileMenuOpen(false); navigate("/angebot"); }}
              className="w-full mt-4 bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full"
              data-testid="nav-cta-mobile"
            >
              Angebot anfragen
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section with Slideshow Background
const HERO_SLIDES = [
  { src: "/hero-treppenhaus.jpg", alt: "Treppenhausreinigung Frankfurt durch Frankfurt Reinigung 24" },
  { src: "/hero-buero.jpg", alt: "Büroreinigung Frankfurt durch Frankfurt Reinigung 24" },
  { src: "/hero-wohnung.jpg", alt: "Wohnungsreinigung Frankfurt durch Frankfurt Reinigung 24" },
  { src: "/hero-fenster.jpg", alt: "Fensterreinigung Frankfurt durch Frankfurt Reinigung 24" },
  { src: "/hero-hausmeister.jpg", alt: "Hausmeisterservice Frankfurt durch Frankfurt Reinigung 24" },
];

const HeroSection = () => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActiveSlide((i) => (i + 1) % HERO_SLIDES.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Slideshow background */}
      <div className="absolute inset-0" data-testid="hero-slideshow">
        {HERO_SLIDES.map((slide, idx) => (
          <img
            key={slide.src}
            src={slide.src}
            alt={slide.alt}
            data-testid={`hero-slide-${idx}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out ${
              activeSlide === idx ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </div>
      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/85 via-[#0F172A]/60 to-[#0F172A]/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/40 via-transparent to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
        <div className="max-w-2xl">
          <span className="inline-block text-[#60A5FA] font-semibold text-sm uppercase tracking-widest mb-4 animate-fadeInUp">
            Seit 2002 in Frankfurt
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fadeInUp animation-delay-100">
            Professioneller Reinigungs- &amp; Hausmeisterservice in Frankfurt
          </h1>
          <p className="text-lg sm:text-xl text-white/90 font-medium leading-relaxed mb-8 animate-fadeInUp animation-delay-200">
            Zuverlässige Reinigung, Pflege und Betreuung von Immobilien – seit über 20 Jahren.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp animation-delay-300">
            <Button
              onClick={() => navigate("/angebot")}
              className="btn-primary bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full px-8 py-6 text-lg font-medium shadow-2xl shadow-[#0066CC]/40"
            >
              Kostenloses Angebot <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => navigate("/leistungen")}
              variant="outline"
              className="btn-secondary bg-white/10 backdrop-blur-md text-white border-2 border-white/30 hover:bg-white hover:text-[#0F172A] rounded-full px-8 py-6 text-lg font-medium"
            >
              Unsere Leistungen
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-6 mt-12 animate-fadeInUp animation-delay-400">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#10B981]" />
              <span className="text-white/90 font-medium">20+ Jahre Erfahrung</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#10B981]" />
              <span className="text-white/90 font-medium">Kostenlose Beratung</span>
            </div>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10" data-testid="hero-indicators">
        {HERO_SLIDES.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setActiveSlide(idx)}
            aria-label={`Slide ${idx + 1}`}
            data-testid={`hero-indicator-${idx}`}
            className={`h-2 rounded-full transition-all duration-300 ${
              activeSlide === idx ? "w-10 bg-white" : "w-2 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const strengths = [
    { icon: Shield, title: "Zuverlässigkeit", desc: "Pünktlich und gewissenhaft" },
    { icon: Award, title: "Erfahrung", desc: "Über 20 Jahre am Markt" },
    { icon: Clock, title: "Flexible Termine", desc: "Nach Ihren Wünschen" },
    { icon: Users, title: "Persönlicher Service", desc: "Fester Ansprechpartner" },
  ];

  return (
    <section id="ueber-uns" data-testid="about-section" className="py-20 md:py-32 bg-[#F8FAFC]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="text-[#0066CC] font-semibold text-sm uppercase tracking-widest">Über Uns</span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mt-3 mb-6">
          Ihr Partner für Sauberkeit in Frankfurt
        </h2>
        <p className="text-lg text-[#64748B] leading-relaxed mb-12 max-w-3xl mx-auto">
          Frankfurt Reinigung 24 ist ein erfahrenes Dienstleistungsunternehmen aus Frankfurt am Main. 
          Seit über 20 Jahren bieten wir professionelle Reinigungs- und Hausmeisterdienstleistungen 
          für Privatkunden, Unternehmen und Hausverwaltungen in Frankfurt und der Rhein-Main-Region.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
          {strengths.map((strength, index) => (
            <div key={index} className="text-center">
              <div className="w-14 h-14 bg-[#0066CC] rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-[#0066CC]/20">
                <strength.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-semibold text-[#0F172A] mb-1">{strength.title}</h3>
              <p className="text-sm text-[#64748B]">{strength.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section with Links
const ServicesSection = () => {
  const services = SEO_DATA.services.slice(0, 4);

  return (
    <section id="leistungen" data-testid="services-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#0066CC] font-semibold text-sm uppercase tracking-widest">Unsere Leistungen</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mt-3">
            Rundum-Service für Ihre Immobilie
          </h2>
          <p className="text-lg text-[#64748B] mt-4 max-w-2xl mx-auto">
            Von der Gebäudereinigung bis zum Winterdienst - wir bieten alle Dienstleistungen aus einer Hand.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <Link key={index} to={`/leistungen/${service.slug}`}>
              <Card className="service-card h-full bg-white border border-slate-100 hover:border-[#0066CC]/30 rounded-2xl overflow-hidden">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-[#E0F2FE] rounded-2xl flex items-center justify-center mb-4">
                    <service.icon className="w-7 h-7 text-[#0066CC]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">{service.name}</h3>
                  <p className="text-[#64748B] text-sm mb-4">{service.description.slice(0, 80)}...</p>
                  <span className="text-[#0066CC] font-medium flex items-center gap-1">
                    Mehr erfahren <ArrowRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/leistungen">
            <Button variant="outline" className="border-2 border-[#0066CC] text-[#0066CC] hover:bg-[#0066CC] hover:text-white rounded-full px-8 py-6">
              Alle Leistungen ansehen <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Why Us Section
const WhyUsSection = () => {
  const reasons = [
    { title: "Über 20 Jahre Erfahrung", desc: "Bewährte Kompetenz seit 2002" },
    { title: "Zuverlässige Mitarbeiter", desc: "Geschultes Fachpersonal" },
    { title: "Flexible Betreuung", desc: "Angepasst an Ihre Bedürfnisse" },
    { title: "Individuelle Angebote", desc: "Maßgeschneiderte Lösungen" },
    { title: "Persönlicher Ansprechpartner", desc: "Direkter Kontakt garantiert" },
  ];

  return (
    <section id="warum-wir" data-testid="why-us-section" className="py-20 md:py-32 bg-[#0F172A] text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#60A5FA] font-semibold text-sm uppercase tracking-widest">Warum Wir</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 mb-6">
            Ihre Vorteile mit Frankfurt Reinigung 24
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
            Wir setzen auf Qualität, Zuverlässigkeit und persönlichen Service in Frankfurt und der Rhein-Main-Region.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {reasons.map((reason, index) => (
            <div key={index} className="flex items-start gap-4 p-5 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 hover:border-[#0066CC]/50 transition-all">
              <div className="w-10 h-10 bg-[#0066CC] rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">{reason.title}</h3>
                <p className="text-sm text-slate-400">{reason.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Locations Teaser Section
const LocationsTeaser = () => {
  const featuredLocations = SEO_DATA.locations.slice(0, 8);

  return (
    <section className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#0066CC] font-semibold text-sm uppercase tracking-widest">Unsere Standorte</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mt-3">
            Reinigungsservice in Frankfurt & Umgebung
          </h2>
          <p className="text-lg text-[#64748B] mt-4 max-w-2xl mx-auto">
            Wir sind in ganz Frankfurt und der Rhein-Main-Region für Sie da.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {featuredLocations.map((location, index) => (
            <Link key={index} to={`/standorte/${location.slug}`}>
              <div className="p-4 bg-[#F8FAFC] rounded-xl hover:bg-[#E0F2FE] transition-colors text-center">
                <MapPin className="w-6 h-6 text-[#0066CC] mx-auto mb-2" />
                <p className="font-medium text-[#0F172A]">{location.name}</p>
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/standorte">
            <Button variant="outline" className="border-2 border-[#0066CC] text-[#0066CC] hover:bg-[#0066CC] hover:text-white rounded-full px-8 py-6">
              Alle Standorte <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Customers Section
const CustomersSection = () => {
  const customers = [
    { icon: Building2, title: "Hausverwaltungen", desc: "Professionelle Betreuung von Mehrfamilienhäusern" },
    { icon: Users, title: "Eigentümergemeinschaften", desc: "Zuverlässiger Service für WEGs" },
    { icon: Briefcase, title: "Unternehmen", desc: "Gewerbliche Reinigung und Pflege" },
    { icon: Home, title: "Privatkunden", desc: "Individuelle Lösungen für Ihr Zuhause" },
  ];

  return (
    <section id="kunden" data-testid="customers-section" className="py-20 md:py-32 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#0066CC] font-semibold text-sm uppercase tracking-widest">Für Wen Wir Arbeiten</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mt-3">Unsere Kunden in Frankfurt</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {customers.map((customer, index) => (
            <Card key={index} className="service-card bg-white border-none shadow-sm hover:shadow-lg rounded-2xl text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#E0F2FE] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <customer.icon className="w-8 h-8 text-[#0066CC]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-2">{customer.title}</h3>
                <p className="text-[#64748B]">{customer.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const images = [
    { url: "/fenster.jpg", alt: "Fensterreinigung Frankfurt", label: "Fensterreinigung" },
    { url: "https://images.unsplash.com/photo-1759698564295-a615e9c9aad4?w=600", alt: "Gebäudepflege Frankfurt", label: "Gebäudepflege" },
    { url: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600", alt: "Treppenhausreinigung Frankfurt", label: "Treppenhausreinigung" },
    { url: "/buero.jpg", alt: "Büroreinigung Frankfurt", label: "Büroreinigung" },
    { url: "https://images.unsplash.com/photo-1734303023491-db8037a21f09?w=600", alt: "Gartenpflege Frankfurt", label: "Gartenpflege" },
    { url: "/wohnung.jpeg", alt: "Wohnungsreinigung Frankfurt", label: "Wohnungsreinigung" },
  ];

  return (
    <section id="galerie" data-testid="gallery-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#0066CC] font-semibold text-sm uppercase tracking-widest">Galerie</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mt-3">Unsere Arbeit in Bildern</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((image, index) => (
            <div key={index} className="group relative aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
              <img src={image.url} alt={image.alt} className="gallery-image w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-white font-semibold text-lg">{image.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    { name: "Yvonne Näfe", date: "23.12.2019", text: "Perfekter Service: Alles wird zuverlässig und schnell geklärt - auch außerhalb der üblichen Arbeitszeiten. Ich bin mehr als zufrieden!", rating: 5 },
    { name: "Max Bleimling", date: "24.10.2019", text: "Immer zuverlässig und gründlich, kann ich nur weiter empfehlen!", rating: 5 },
    { name: "Andreas Schwarz", date: "04.10.2019", text: "Die Grundreinigung unseres Treppenhauses wurde perfekt erledigt. Wie neu! Gerne wieder.", rating: 5 },
    { name: "Kübra Yilmaz", date: "23.07.2019", text: "Ein sehr professionelles Reinigungsteam, welches stets zuverlässig arbeitet. Definitiv empfehlenswert!", rating: 5 },
    { name: "Familie Müller", date: "15.03.2020", text: "Seit über einem Jahr betreut Frankfurt Reinigung 24 unsere Wohnanlage. Die Zusammenarbeit ist ausgezeichnet.", rating: 5 },
    { name: "Thomas Weber", date: "08.09.2021", text: "Schnelle Reaktionszeiten und kompetente Ausführung. Der Winterdienst hat letzten Winter einwandfrei funktioniert.", rating: 5 },
  ];

  return (
    <section id="feedbacks" data-testid="testimonials-section" className="py-20 md:py-32 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#0066CC] font-semibold text-sm uppercase tracking-widest">Kundenstimmen</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mt-3">Das sagen unsere Kunden</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="bg-white border-none shadow-sm hover:shadow-lg transition-all duration-300 rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-[#0066CC]/20 mb-3" />
                <p className="text-[#64748B] leading-relaxed mb-4">"{testimonial.text}"</p>
                <div className="border-t border-slate-200 pt-4">
                  <p className="font-semibold text-[#0F172A]">{testimonial.name}</p>
                  <p className="text-sm text-[#94A3B8]">{testimonial.date}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Jobs Section
const JobsSection = () => {
  const jobs = [
    { title: "Reinigungskraft (m/w/d)", type: "Teilzeit / Minijob", tasks: ["Treppenhausreinigung", "Gebäudereinigung", "Büroreinigung"], requirements: ["Zuverlässigkeit", "Flexibilität"] },
    { title: "Hausmeister (m/w/d)", type: "Teilzeit / Vollzeit", tasks: ["Betreuung von Wohnanlagen", "Müllbereitstellung", "Winterdienst", "Gartenpflege", "Kleine Reparaturen"], requirements: ["Zuverlässigkeit", "Handwerkliches Geschick", "Führerschein von Vorteil"] },
  ];

  return (
    <section id="jobs" data-testid="jobs-section" className="py-20 md:py-32 bg-gradient-to-br from-[#E0F2FE] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#0066CC] font-semibold text-sm uppercase tracking-widest">Karriere</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mt-3">Werden Sie Teil unseres Teams</h2>
          <p className="text-lg text-[#64748B] mt-4">Wir suchen engagierte Mitarbeiter in Frankfurt und Umgebung.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {jobs.map((job, index) => (
            <Card key={index} className="bg-white rounded-2xl shadow-lg border-none">
              <CardContent className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-[#0F172A]">{job.title}</h3>
                  <span className="text-sm bg-[#E0F2FE] text-[#0066CC] px-3 py-1 rounded-full font-medium">{job.type}</span>
                </div>
                <h4 className="font-semibold text-[#0F172A] mb-3">Ihre Aufgaben:</h4>
                <ul className="space-y-2 mb-6">
                  {job.tasks.map((task, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#64748B]">
                      <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" /><span>{task}</span>
                    </li>
                  ))}
                </ul>
                <h4 className="font-semibold text-[#0F172A] mb-3">Was wir erwarten:</h4>
                <ul className="space-y-2">
                  {job.requirements.map((req, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#64748B]">
                      <CheckCircle className="w-5 h-5 text-[#0066CC] flex-shrink-0" /><span>{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Card className="bg-white rounded-2xl shadow-lg border-none inline-block">
            <CardContent className="p-8">
              <p className="text-[#64748B] mb-4">Interesse? Bewerben Sie sich per E-Mail:</p>
              <a href="mailto:selina.pfeiffer@frankfurt-reinigung24.de?subject=Bewerbung" className="inline-flex items-center gap-2 text-[#0066CC] font-semibold hover:underline text-lg">
                <Mail className="w-6 h-6" />selina.pfeiffer@frankfurt-reinigung24.de
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// Contact Section with Netlify Forms
const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const form = e.target;
    const formData = new FormData(form);
    
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });
      setIsSubmitted(true);
      toast.success("Vielen Dank! Wir melden uns in Kürze bei Ihnen.");
      form.reset();
    } catch (error) {
      toast.error("Es gab ein Problem. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="py-20 md:py-32 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#0066CC] font-semibold text-sm uppercase tracking-widest">Kontakt</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mt-3">Kostenloses Angebot anfordern</h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-12">
          <Card className="bg-white shadow-xl rounded-2xl border-none">
            <CardContent className="p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-[#0F172A] mb-2">Vielen Dank!</h3>
                  <p className="text-[#64748B]">Wir haben Ihre Anfrage erhalten und melden uns in Kürze bei Ihnen.</p>
                </div>
              ) : (
                <form name="contact" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={handleSubmit} className="space-y-6">
                  <input type="hidden" name="form-name" value="contact" />
                  <p className="hidden"><label>Don't fill this out: <input name="bot-field" /></label></p>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-[#0F172A] mb-2">Name *</label>
                    <Input id="name" name="name" type="text" required placeholder="Ihr Name" className="h-12 bg-[#F8FAFC] border-slate-200 rounded-lg" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-[#0F172A] mb-2">Telefon *</label>
                    <Input id="phone" name="phone" type="tel" required placeholder="Ihre Telefonnummer" className="h-12 bg-[#F8FAFC] border-slate-200 rounded-lg" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#0F172A] mb-2">E-Mail *</label>
                    <Input id="email" name="email" type="email" required placeholder="Ihre E-Mail-Adresse" className="h-12 bg-[#F8FAFC] border-slate-200 rounded-lg" />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-[#0F172A] mb-2">Nachricht *</label>
                    <Textarea id="message" name="message" required placeholder="Wie können wir Ihnen helfen?" rows={5} className="bg-[#F8FAFC] border-slate-200 rounded-lg resize-none" />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full py-6 text-lg font-medium shadow-lg">
                    {isSubmitting ? "Wird gesendet..." : <><Send className="mr-2 w-5 h-5" />Nachricht senden</>}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
          <div className="space-y-6">
            <Card className="bg-white shadow-xl rounded-2xl border-none">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-[#0F172A] mb-6">Kontaktdaten</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#E0F2FE] rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#0066CC]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0F172A]">Adresse</p>
                      <p className="text-[#64748B]">Käthchen-Paulus-Straße 20</p>
                      <p className="text-[#64748B]">60486 Frankfurt am Main</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#E0F2FE] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#0066CC]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0F172A]">Telefon</p>
                      <a href="tel:+4916092423550" className="text-[#0066CC] hover:underline">+49 160 92423550</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#DCFCE7] rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#25D366" className="w-6 h-6">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-[#0F172A]">WhatsApp</p>
                      <a
                        href="https://api.whatsapp.com/send?phone=4916092423550&text=Hallo%20Frankfurt%20Reinigung%2024%2C%20ich%20interessiere%20mich%20f%C3%BCr%20Ihre%20Leistungen%20und%20h%C3%A4tte%20gerne%20ein%20unverbindliches%20Angebot."
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="whatsapp-contact-link"
                        className="text-[#25D366] hover:underline font-medium"
                      >
                        +49 160 92423550
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#E0F2FE] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#0066CC]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0F172A]">E-Mail</p>
                      <a href="mailto:selina.pfeiffer@frankfurt-reinigung24.de" className="text-[#0066CC] hover:underline">selina.pfeiffer@frankfurt-reinigung24.de</a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-xl rounded-2xl border-none overflow-hidden">
              <div className="aspect-[16/9] relative">
                <iframe title="Standort Frankfurt Reinigung 24" src="https://www.google.com/maps?q=K%C3%A4thchen-Paulus-Stra%C3%9Fe+20,+60486+Frankfurt+am+Main&output=embed" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" className="absolute inset-0" />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#0F172A] text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4 bg-white/10 backdrop-blur-sm rounded-xl p-3 inline-flex">
              <img src="/logo.png" alt="Frankfurt Reinigung 24 Logo" className="h-12 w-auto" />
              <span className="font-bold text-lg">Frankfurt Reinigung 24</span>
            </div>
            <p className="text-slate-400 mb-4">Ihr zuverlässiger Partner für Reinigung und Hausmeisterservice in Frankfurt seit 2002.</p>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-4">Leistungen</h3>
            <ul className="space-y-2 text-slate-400">
              {SEO_DATA.services.slice(0, 7).map((service, i) => (
                <li key={i}><Link to={`/leistungen/${service.slug}`} className="hover:text-white">{service.name}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-4">Standorte</h3>
            <ul className="space-y-2 text-slate-400">
              {SEO_DATA.locations.slice(0, 6).map((location, i) => (
                <li key={i}><Link to={`/standorte/${location.slug}`} className="hover:text-white">{location.name}</Link></li>
              ))}
              <li><Link to="/standorte" className="text-[#0066CC] hover:text-white">Alle Standorte →</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-4">Kontakt</h3>
            <ul className="space-y-3 text-slate-400">
              <li className="flex items-center gap-2"><MapPin className="w-5 h-5 text-[#0066CC]" />Käthchen-Paulus-Straße 20, Frankfurt</li>
              <li className="flex items-center gap-2"><Phone className="w-5 h-5 text-[#0066CC]" /><a href="tel:+4916092423550" className="hover:text-white">+49 160 92423550</a></li>
              <li className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#0066CC]" /><a href="mailto:selina.pfeiffer@frankfurt-reinigung24.de" className="hover:text-white text-sm">selina.pfeiffer@...</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-slate-500 text-sm">
          <p>© {currentYear} Frankfurt Reinigung 24. Alle Rechte vorbehalten.</p>
          <div className="flex items-center gap-6">
            <Link to="/impressum" className="hover:text-white transition-colors">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-white transition-colors">Datenschutz</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Service Detail Page
const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = SEO_DATA.services.find(s => s.slug === slug);

  useEffect(() => {
    if (service) {
      document.title = service.title;
    }
  }, [service]);

  if (!service) return <div className="pt-32 text-center">Service nicht gefunden</div>;

  return (
    <div className="min-h-screen bg-white">
      <Navigation isSubpage={true} />
      <div className="pt-20 bg-gradient-to-br from-[#0066CC] to-[#004080] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate("/leistungen")} className="flex items-center gap-2 text-white/70 hover:text-white mb-6">
            <ArrowLeft className="w-5 h-5" />Alle Leistungen
          </button>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <service.icon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold">{service.name} Frankfurt</h1>
          </div>
          <p className="text-xl text-white/80 max-w-2xl">{service.heroText}</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">{service.name} in Frankfurt am Main</h2>
            <p className="text-[#64748B] leading-relaxed mb-8">{service.content}</p>
            <p className="text-[#64748B] leading-relaxed mb-8">
              {service.slug === "malerarbeiten"
                ? "Wir übernehmen Innenanstriche für Wohnungen, Büros, Praxen, Studios und Treppenhäuser. Den endgültigen Arbeitsumfang bestätigen wir anhand Ihrer Angaben, Fotos oder einer Besichtigung."
                : <>Als erfahrene Reinigungsfirma in Frankfurt bieten wir professionelle {service.name.toLowerCase()} für Privatkunden, Hausverwaltungen und Unternehmen. Seit 2002 sind wir Ihr zuverlässiger Partner in Frankfurt und der gesamten Rhein-Main-Region.</>}
            </p>
            <h3 className="text-xl font-bold text-[#0F172A] mb-4">Unsere Leistungen im Bereich {service.name}</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {service.benefits.map((benefit, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-xl">
                  <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                  <span className="text-[#64748B]">{benefit}</span>
                </div>
              ))}
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-4">{service.name} in Ihrer Nähe</h3>
            <p className="text-[#64748B] leading-relaxed mb-4">
              Wir bieten {service.name.toLowerCase()} in ganz Frankfurt und Umgebung an. Egal ob Sie in Bockenheim, Westend, 
              Sachsenhausen oder einem anderen Stadtteil wohnen - wir sind schnell bei Ihnen.
            </p>
            <div className="flex flex-wrap gap-2 mb-8">
              {SEO_DATA.locations.slice(0, 10).map((loc, i) => (
                <Link key={i} to={`/standorte/${loc.slug}`} className="px-3 py-1 bg-[#E0F2FE] text-[#0066CC] rounded-full text-sm hover:bg-[#0066CC] hover:text-white transition-colors">
                  {loc.name}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <Card className="bg-[#F8FAFC] border-none rounded-2xl sticky top-24">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#0F172A] mb-4">Kostenloses Angebot</h3>
                <p className="text-[#64748B] mb-6">Fordern Sie jetzt ein unverbindliches Angebot für {service.name.toLowerCase()} in Frankfurt an.</p>
                <Button onClick={() => navigate(service.slug === "malerarbeiten" ? "/angebot" : "/#kontakt")} className="w-full bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full py-6">
                  {service.slug === "malerarbeiten" ? "Malerarbeiten kalkulieren" : "Angebot anfordern"} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="text-sm text-[#64748B] mb-2">Oder rufen Sie uns an:</p>
                  <a href="tel:+4916092423550" className="flex items-center gap-2 text-[#0066CC] font-semibold">
                    <Phone className="w-5 h-5" />+49 160 92423550
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Services Overview Page
const ServicesOverviewPage = () => {
  useEffect(() => {
    document.title = "Reinigung & Malerarbeiten Frankfurt | Frankfurt Reinigung 24";
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation isSubpage={true} />
      <div className="pt-20 bg-gradient-to-br from-[#0066CC] to-[#004080] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Unsere Leistungen</h1>
          <p className="text-xl text-white/80 max-w-2xl">Professionelle Reinigung, Hausmeisterservice und Malerarbeiten in Frankfurt am Main – alle Leistungen aus einer Hand.</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SEO_DATA.services.map((service, index) => (
            <Link key={index} to={`/leistungen/${service.slug}`}>
              <Card className="h-full service-card bg-white border border-slate-100 hover:border-[#0066CC]/30 rounded-2xl">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-[#E0F2FE] rounded-2xl flex items-center justify-center mb-4">
                    <service.icon className="w-7 h-7 text-[#0066CC]" />
                  </div>
                  <h2 className="text-xl font-bold text-[#0F172A] mb-2">{service.name}</h2>
                  <p className="text-[#64748B] text-sm mb-4">{service.description}</p>
                  <span className="text-[#0066CC] font-medium flex items-center gap-1">
                    Mehr erfahren <ArrowRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Location Detail Page
const LocationPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = SEO_DATA.locations.find(l => l.slug === slug);

  useEffect(() => {
    if (location) {
      document.title = `Reinigungsservice ${location.name} | Frankfurt Reinigung 24`;
    }
  }, [location]);

  if (!location) return <div className="pt-32 text-center">Standort nicht gefunden</div>;

  return (
    <div className="min-h-screen bg-white">
      <Navigation isSubpage={true} />
      <div className="pt-20 bg-gradient-to-br from-[#0066CC] to-[#004080] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate("/standorte")} className="flex items-center gap-2 text-white/70 hover:text-white mb-6">
            <ArrowLeft className="w-5 h-5" />Alle Standorte
          </button>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold">Reinigungsservice {location.name}</h1>
          </div>
          <p className="text-xl text-white/80 max-w-2xl">Professionelle Gebäudereinigung und Hausmeisterservice in {location.name} und Umgebung.</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Ihre Reinigungsfirma in {location.name}</h2>
            <p className="text-[#64748B] leading-relaxed mb-8">
              Frankfurt Reinigung 24 ist Ihr zuverlässiger Partner für professionelle Reinigungsdienstleistungen in {location.name}. 
              Seit 2002 betreuen wir Privatkunden, Hausverwaltungen und Unternehmen in der Region mit erstklassigem Service.
            </p>
            <p className="text-[#64748B] leading-relaxed mb-8">
              Ob Gebäudereinigung, Treppenhausreinigung, Büroreinigung oder Hausmeisterservice - wir bieten alle Leistungen aus einer Hand. 
              Unsere erfahrenen Teams sind schnell vor Ort und sorgen für makellose Sauberkeit in {location.name}.
            </p>
            <h3 className="text-xl font-bold text-[#0F172A] mb-4">Unsere Leistungen in {location.name}</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {SEO_DATA.services.map((service, i) => (
                <Link key={i} to={`/leistungen/${service.slug}`} className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-xl hover:bg-[#E0F2FE] transition-colors">
                  <service.icon className="w-5 h-5 text-[#0066CC] flex-shrink-0" />
                  <span className="text-[#64748B]">{service.name}</span>
                </Link>
              ))}
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-4">Weitere Standorte in der Nähe</h3>
            <div className="flex flex-wrap gap-2">
              {SEO_DATA.locations.filter(l => l.slug !== slug).slice(0, 8).map((loc, i) => (
                <Link key={i} to={`/standorte/${loc.slug}`} className="px-3 py-1 bg-[#E0F2FE] text-[#0066CC] rounded-full text-sm hover:bg-[#0066CC] hover:text-white transition-colors">
                  {loc.name}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <Card className="bg-[#F8FAFC] border-none rounded-2xl sticky top-24">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#0F172A] mb-4">Kostenloses Angebot für {location.name}</h3>
                <p className="text-[#64748B] mb-6">Fordern Sie jetzt ein unverbindliches Angebot für Reinigungsservice in {location.name} an.</p>
                <Button onClick={() => navigate("/#kontakt")} className="w-full bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full py-6">
                  Angebot anfordern <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="text-sm text-[#64748B] mb-2">Oder rufen Sie uns an:</p>
                  <a href="tel:+4916092423550" className="flex items-center gap-2 text-[#0066CC] font-semibold">
                    <Phone className="w-5 h-5" />+49 160 92423550
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Locations Overview Page
const LocationsOverviewPage = () => {
  useEffect(() => {
    document.title = "Reinigungsservice Frankfurt & Umgebung | Frankfurt Reinigung 24";
  }, []);

  const frankfurtLocations = SEO_DATA.locations.filter(l => l.region === "Frankfurt" || l.region === "Zentrum");
  const surroundingLocations = SEO_DATA.locations.filter(l => l.region === "Umgebung");

  return (
    <div className="min-h-screen bg-white">
      <Navigation isSubpage={true} />
      <div className="pt-20 bg-gradient-to-br from-[#0066CC] to-[#004080] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Unsere Standorte</h1>
          <p className="text-xl text-white/80 max-w-2xl">Reinigungsservice und Hausmeisterservice in Frankfurt am Main und der gesamten Rhein-Main-Region.</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Frankfurt am Main</h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
          {frankfurtLocations.map((location, index) => (
            <Link key={index} to={`/standorte/${location.slug}`}>
              <Card className="service-card bg-white border border-slate-100 hover:border-[#0066CC]/30 rounded-xl">
                <CardContent className="p-4 text-center">
                  <MapPin className="w-6 h-6 text-[#0066CC] mx-auto mb-2" />
                  <p className="font-medium text-[#0F172A]">{location.name}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Rhein-Main-Region</h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {surroundingLocations.map((location, index) => (
            <Link key={index} to={`/standorte/${location.slug}`}>
              <Card className="service-card bg-white border border-slate-100 hover:border-[#0066CC]/30 rounded-xl">
                <CardContent className="p-4 text-center">
                  <MapPin className="w-6 h-6 text-[#0066CC] mx-auto mb-2" />
                  <p className="font-medium text-[#0F172A]">{location.name}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Impressum Page
const ImpressumPage = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "Impressum | Frankfurt Reinigung 24"; }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation isSubpage={true} />
      <div className="pt-20 bg-[#0F172A] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6"><ArrowLeft className="w-5 h-5" />Zurück</button>
          <h1 className="text-4xl sm:text-5xl font-bold text-white">Impressum</h1>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Angaben gemäß § 5 DDG</h2>
            <div className="text-[#64748B] space-y-1">
              <p className="font-semibold text-[#0F172A]">Frankfurt Reinigung 24</p>
              <p>Inhaberin: Selina Pfeiffer</p>
              <p>Käthchen-Paulus-Straße 20</p>
              <p>60486 Frankfurt am Main</p>
              <p>Deutschland</p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Kontakt</h2>
            <div className="text-[#64748B] space-y-2">
              <p>E-Mail: <a href="mailto:selina.pfeiffer@frankfurt-reinigung24.de" className="text-[#0066CC] hover:underline">selina.pfeiffer@frankfurt-reinigung24.de</a></p>
              <p>Telefon: <a href="tel:+4916092423550" className="text-[#0066CC] hover:underline">0160 92423550</a></p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2>
            <div className="text-[#64748B] space-y-1">
              <p>Selina Pfeiffer</p>
              <p>Käthchen-Paulus-Straße 20</p>
              <p>60486 Frankfurt am Main</p>
              <p>Deutschland</p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Verbraucherstreitbeilegung</h2>
            <p className="text-[#64748B]">Wir sind nicht verpflichtet und nicht bereit, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Datenschutz Page
const DatenschutzPage = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "Datenschutz | Frankfurt Reinigung 24"; }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation isSubpage={true} />
      <div className="pt-20 bg-[#0F172A] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6"><ArrowLeft className="w-5 h-5" />Zurück</button>
          <h1 className="text-4xl sm:text-5xl font-bold text-white">Datenschutzerklärung</h1>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none text-[#64748B]">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">1. Datenschutz auf einen Blick</h2>
            <p>Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen.</p>
          </section>
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">2. Verantwortlicher</h2>
            <div className="bg-[#F8FAFC] p-6 rounded-xl">
              <p className="font-semibold text-[#0F172A]">Frankfurt Reinigung 24 – Inhaberin: Selina Pfeiffer</p>
              <p>Käthchen-Paulus-Straße 20, 60486 Frankfurt am Main</p>
              <p>Telefon: 0160 92423550</p>
              <p>E-Mail: selina.pfeiffer@frankfurt-reinigung24.de</p>
            </div>
          </section>
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">3. Kontaktformular</h2>
            <p>Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben zwecks Bearbeitung der Anfrage bei uns gespeichert. Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO.</p>
          </section>
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">4. Ihre Rechte</h2>
            <p>Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch. Bei Fragen wenden Sie sich an uns.</p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// FAQ Section - matches schema.org FAQPage in index.html for Rich Snippets
const FaqSection = () => {
  const faqs = [
    {
      q: "Was kostet eine Reinigungsfirma in Frankfurt?",
      a: "Die Kosten richten sich nach Objektgröße, Reinigungsintervall und Umfang. Treppenhausreinigung und Büroreinigung werden bei uns immer individuell kalkuliert – passend zu Ihrem Objekt und Bedarf. Bei uns erhalten Sie ein kostenloses und unverbindliches Angebot innerhalb von 24 Stunden."
    },
    {
      q: "Welche Reinigungsleistungen bieten Sie an?",
      a: "Wir bieten das komplette Leistungsspektrum: Gebäudereinigung, Treppenhausreinigung, Büroreinigung, Wohnungsreinigung, Fenster- und Glasreinigung, Hausmeisterservice, Winterdienst, Gartenpflege und Malerarbeiten im Innenbereich – für Privatkunden, Wohnanlagen, Hausverwaltungen und Gewerbeimmobilien."
    },
    {
      q: "In welchen Stadtteilen Frankfurts sind Sie tätig?",
      a: "Unsere Teams sind in ganz Frankfurt am Main im Einsatz: Bockenheim, Westend, Nordend, Sachsenhausen, Bornheim, Ostend, Gallus, Bahnhofsviertel, Innenstadt und allen weiteren Stadtteilen. Außerdem reinigen wir in Offenbach, Bad Homburg, Eschborn, Oberursel, Kronberg, Neu-Isenburg, Dreieich, Langen, Maintal und Hanau."
    },
    {
      q: "Wie schnell bekomme ich ein Angebot?",
      a: "Sie erhalten innerhalb von 24 Stunden ein kostenloses und unverbindliches Angebot. Bei dringenden Anfragen erreichen Sie uns telefonisch unter +49 160 92423550 – auch außerhalb der üblichen Geschäftszeiten. Auf Wunsch besichtigen wir Ihr Objekt vor Ort kostenlos."
    },
    {
      q: "Sind Sie eine zertifizierte Reinigungsfirma?",
      a: "Ja, Frankfurt Reinigung 24 ist seit 2002 als professionelle Reinigungs- und Hausmeisterfirma in Frankfurt tätig. Wir sind vollständig versichert (Betriebshaftpflicht), unser Personal ist fest angestellt und mit modernster Reinigungstechnik ausgestattet."
    },
    {
      q: "Wer übernimmt die Treppenhausreinigung in Frankfurter Mehrfamilienhäusern?",
      a: "Wir reinigen seit über 20 Jahren Treppenhäuser für Hausverwaltungen, WEGs und Privateigentümer in Frankfurt. Die Reinigung erfolgt in festen Intervallen (wöchentlich, 14-tägig oder nach Vereinbarung) und umfasst das komplette Treppenhaus inkl. Geländer, Briefkasten, Eingangstüren und Fenster."
    },
    {
      q: "Bieten Sie auch Hausmeisterservice und Winterdienst an?",
      a: "Ja, neben der Reinigung übernehmen wir den kompletten Hausmeisterservice: kleine Reparaturen, technische Kontrollen, Müllmanagement, Außenanlagenpflege sowie zuverlässigen Winterdienst (Schneeräumen und Streuen) gemäß Räum- und Streupflicht der Stadt Frankfurt."
    },
    {
      q: "Wie kann ich Sie kontaktieren?",
      a: "Sie erreichen uns telefonisch und per WhatsApp unter +49 160 92423550, per E-Mail an selina.pfeiffer@frankfurt-reinigung24.de oder direkt über das Kontaktformular auf dieser Seite. Unser Büro: Käthchen-Paulus-Straße 20, 60486 Frankfurt am Main."
    }
  ];

  return (
    <section id="faq" data-testid="faq-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#0066CC] font-semibold text-sm uppercase tracking-widest">FAQ</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mt-3">Häufig gestellte Fragen</h2>
          <p className="text-lg text-[#64748B] mt-4">Antworten zu Reinigung, Hausmeisterservice und Preisen in Frankfurt</p>
        </div>
        <Accordion type="single" collapsible className="w-full space-y-4" data-testid="faq-accordion">
          {faqs.map((item, index) => (
            <AccordionItem
              key={index}
              value={`faq-${index}`}
              data-testid={`faq-item-${index}`}
              className="bg-[#F8FAFC] border border-slate-200 rounded-2xl px-6 data-[state=open]:bg-white data-[state=open]:shadow-lg transition-all"
            >
              <AccordionTrigger className="text-left text-base sm:text-lg font-semibold text-[#0F172A] hover:no-underline py-5">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#64748B] leading-relaxed pb-5 text-base">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="text-center mt-12">
          <p className="text-[#64748B] mb-4">Ihre Frage nicht dabei?</p>
          <a
            href="#kontakt"
            data-testid="faq-contact-link"
            className="inline-flex items-center gap-2 text-[#0066CC] font-semibold hover:underline"
          >
            Schreiben Sie uns <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Floating WhatsApp Button - Lead Generation Booster (Desktop only - Mobile uses MobileCallBar)
// Auto-hides near footer so it doesn't block Impressum/Datenschutz links
const WhatsAppButton = () => {
  const phone = "4916092423550"; // 0160 92423550 in international format
  const message = encodeURIComponent(
    "Hallo Frankfurt Reinigung 24, ich interessiere mich für Ihre Leistungen und hätte gerne ein unverbindliches Angebot."
  );
  // api.whatsapp.com/send is more reliable on desktop browsers than wa.me
  const href = `https://api.whatsapp.com/send?phone=${phone}&text=${message}`;
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      // Hide when user is within 200px of the bottom of the page
      const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200;
      setHidden(nearBottom);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Per WhatsApp kontaktieren"
      data-testid="whatsapp-floating-button"
      className={`hidden md:flex fixed bottom-6 right-6 z-50 group items-center gap-3 bg-[#25D366] hover:bg-[#1FB855] text-white rounded-full shadow-2xl shadow-[#25D366]/40 px-5 py-4 transition-all duration-500 hover:scale-105 hover:shadow-[#25D366]/60 ${
        hidden ? "opacity-0 pointer-events-none translate-y-6" : "opacity-100"
      }`}
    >
      <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30 -z-10" aria-hidden="true"></span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="w-7 h-7 flex-shrink-0"
        aria-hidden="true"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
      <span className="hidden sm:inline font-semibold text-base whitespace-nowrap">WhatsApp Chat</span>
    </a>
  );
};

// Sticky Mobile Call-Bar - 1-tap conversion on smartphones
const MobileCallBar = () => {
  const phone = "4916092423550";
  const message = encodeURIComponent(
    "Hallo Frankfurt Reinigung 24, ich interessiere mich für Ihre Leistungen und hätte gerne ein unverbindliches Angebot."
  );
  const waHref = `https://api.whatsapp.com/send?phone=${phone}&text=${message}`;

  return (
    <div
      data-testid="mobile-call-bar"
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] grid grid-cols-2"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <a
        href="tel:+4916092423550"
        data-testid="mobile-call-button"
        aria-label="Jetzt anrufen"
        className="flex items-center justify-center gap-2 bg-[#0066CC] hover:bg-[#0052A3] active:bg-[#003D7A] text-white py-4 font-semibold transition-colors"
      >
        <Phone className="w-5 h-5" />
        <span>Anrufen</span>
      </a>
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="mobile-whatsapp-button"
        aria-label="Per WhatsApp kontaktieren"
        className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB855] active:bg-[#168A41] text-white py-4 font-semibold transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
        <span>WhatsApp</span>
      </a>
    </div>
  );
};

// Homepage
const HomePage = () => {
  useEffect(() => {
    document.title = "Reinigungsfirma Frankfurt | Hausmeisterservice | Frankfurt Reinigung 24";
  }, []);

  return (
    <>
      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <WhyUsSection />
        <LocationsTeaser />
        <CustomersSection />
        <TestimonialsSection />
        <JobsSection />
        <FaqSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/leistungen" element={<ServicesOverviewPage />} />
          <Route path="/leistungen/:slug" element={<ServicePage />} />
          <Route path="/standorte" element={<LocationsOverviewPage />} />
          <Route path="/standorte/:slug" element={<LocationPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/angebot" element={<AngebotPage NavigationComp={Navigation} FooterComp={Footer} />} />
        </Routes>
        <WhatsAppButton />
        <MobileCallBar />
      </BrowserRouter>
    </div>
  );
}

export default App;
